import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import './App.css';
import ProjectBackButton from './components/ProjectBackButton';
import ProjectNextFooter from './components/ProjectNextFooter';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import AccessibilityToolbar from './components/AccessibilityToolbar';
import MiniPlayer from './components/MiniPlayer';
import { MusicPlayerProvider } from './context/MusicPlayerContext';
import PageTransition from './components/PageTransition';
import HomeV2 from './pages/HomeV2';
import AboutPage from './pages/AboutPage';
import ProjectsPage from './pages/ProjectsPage';
import TMFProject from './pages/TMFProject';
import TMFProjectShort from './pages/TMFProjectShort';
import TMFPresentation from './pages/TMFPresentation';
import NFLProject from './pages/NFLProject';
import WayfindingProject from './pages/WayfindingProject';
import CragTechProject from './pages/CragTechProject';
import AgricoProject from './pages/AgricoProject';
import AgricoProjectIframe from './pages/AgricoProjectIframe';
import DriftProject from './pages/DriftProject';
// Cairn temporarily disabled — see the matching comment in projectsData.ts.
// import CairnProject from './pages/CairnProject';
import OutlookProject from './pages/OutlookProject';
import CacheProject from './pages/CacheProject';
import ToTheMoonProject from './pages/ToTheMoonProject';
import CacheShowcaseReel from './components/cache/CacheShowcaseReel';
import DitherGifDemo from './pages/DitherGifDemo';

function App() {
  const [theme] = useState('dark');

  return (
    <BrowserRouter>
      <AppContent theme={theme} />
    </BrowserRouter>
  );
}

interface AppContentProps {
  theme: string;
}

// Routes whose page component force-locks body theme (to 'light', or a
// page-specific variant like HomeV2's 'homev2-light') on mount and
// restores it on unmount. These routes are excluded below so the app
// never fights the page's own lock; every other route stays owned by the
// real theme state here.
const FORCE_LIGHT_ROUTES = new Set([
  '/',
  '/projects/tmf',
  '/projects/tmf/short',
  '/projects/nfl',
  '/projects/wayfinding',
  '/projects/cragtech',
  '/projects/agrico',
  '/projects/agrico/full',
  '/projects/drift',
  // '/projects/cairn', (temporarily disabled — see App.tsx route below)
  '/projects/outlook',
  '/projects/cache',
  '/projects/tothemoon',
]);

function AppContent({ theme }: AppContentProps) {
  const location = useLocation();
  // Routes rendered with no site chrome (Footer/AccessibilityToolbar):
  // capture tooling and standalone page previews under active design review.
  const isChromelessRoute = location.pathname === '/dev/cache-reel' || location.pathname === '/dev/dither-gif' || location.pathname === '/';
  // The site-wide nav bar is retired entirely; individual project pages get
  // just a back button instead, returning to HomeV2 with the work panel
  // already open (see ProjectBackButton).
  const isProjectDetailPage = location.pathname.startsWith('/projects/');
  // /agrico/full is a rigid full-viewport iframe embed with body scroll
  // locked (see AgricoProjectIframe) — an in-flow footer below it would
  // be unreachable, so it keeps just the back button.
  const showProjectFooter = isProjectDetailPage && location.pathname !== '/projects/agrico/full';

  // Owns body theme for every non-forced-light route, and re-applies it on
  // every navigation (not just theme changes). This used to live in App as
  // a theme-only effect with no route awareness, which meant nothing ever
  // corrected a project page's exit-transition race if its own cleanup
  // failed to restore the theme, and worse: since that effect was a plain
  // useEffect (deferred, passive) while project pages force light via
  // useLayoutEffect, the passive effect always ran after and clobbered a
  // freshly-mounted project page back to the ambient theme. Being
  // route-aware fixes both.
  useEffect(() => {
    if (FORCE_LIGHT_ROUTES.has(location.pathname)) return;
    document.body.className = theme;
  }, [location.pathname, theme]);

  return (
    <MusicPlayerProvider>
      <ScrollToTop />
      <div className="App">
        {isProjectDetailPage && <ProjectBackButton />}
        <div className="page-transition-container">
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<PageTransition><HomeV2 theme={theme} /></PageTransition>} />
              <Route path="/home-v2" element={<Navigate to="/" replace state={location.state} />} />
              <Route path="/about" element={<PageTransition><AboutPage /></PageTransition>} />
              <Route path="/projects" element={<PageTransition><ProjectsPage theme={theme} /></PageTransition>} />
              <Route path="/projects/tmf" element={<PageTransition><TMFPresentation theme={theme} /></PageTransition>} />
              <Route path="/projects/tmf/full" element={<PageTransition><TMFProject /></PageTransition>} />
              <Route path="/projects/tmf/short" element={<PageTransition><TMFPresentation theme={theme} /></PageTransition>} />
              <Route path="/projects/nfl" element={<PageTransition><NFLProject theme={theme} /></PageTransition>} />
              <Route path="/projects/wayfinding" element={<PageTransition><WayfindingProject theme={theme} /></PageTransition>} />
              <Route path="/projects/cragtech" element={<PageTransition><CragTechProject theme={theme} /></PageTransition>} />
              <Route path="/projects/agrico" element={<PageTransition><AgricoProject theme={theme} /></PageTransition>} />
              <Route path="/projects/tothemoon" element={<PageTransition><ToTheMoonProject theme={theme} /></PageTransition>} />
              <Route path="/projects/agrico/full" element={<AgricoProjectIframe theme={theme} />} />
              <Route path="/projects/drift" element={<PageTransition><DriftProject theme={theme} /></PageTransition>} />
              {/* <Route path="/projects/cairn" element={<PageTransition><CairnProject theme={theme} /></PageTransition>} /> */}
              <Route path="/projects/outlook" element={<PageTransition><OutlookProject theme={theme} /></PageTransition>} />
              <Route path="/projects/cache" element={<PageTransition><CacheProject theme={theme} /></PageTransition>} />
              <Route path="/dev/cache-reel" element={<CacheShowcaseReel />} />
              <Route path="/dev/dither-gif" element={<DitherGifDemo />} />
            </Routes>
          </AnimatePresence>
        </div>
        {showProjectFooter && <ProjectNextFooter />}
        {!isChromelessRoute && !isProjectDetailPage && <Footer />}
        {!isChromelessRoute && <AccessibilityToolbar />}
        <MiniPlayer dock={!isChromelessRoute} />
      </div>
    </MusicPlayerProvider>
  );
}

export default App;